import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  FolderIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { useToast } from '../../components/UI/ToastProvider';
import { categoryService, Category } from '../../services/categoryService';
import { CreateCategoryModal } from '../../components/Categories/CreateCategoryModal';
import { EditCategoryModal } from '../../components/Categories/EditCategoryModal';
import { DeleteCategoryModal } from '../../components/Categories/DeleteCategoryModal';
import { DocumentListByFilter } from '../../components/Documents/DocumentListByFilter';
import { DocumentNode } from '../../types/documents';

export const DocumentCategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDocumentList, setShowDocumentList] = useState(false);
  const [selectedCategoryForDocuments, setSelectedCategoryForDocuments] = useState<Category | null>(null);
  const { addToast } = useToast();

  const loadCategories = async () => {
    try {
      setIsLoading(true);
      const data = await categoryService.getAllCategories();
      setCategories(data);
    } catch (error) {
      addToast('Erro ao carregar categorias. Tente novamente.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleCreateCategory = () => {
    setShowCreateModal(true);
  };

  const handleEditCategory = (category: Category) => {
    setSelectedCategory(category);
    setShowEditModal(true);
  };

  const handleDeleteCategory = (category: Category) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCategory) return;

    setIsDeleting(true);
    try {
      await categoryService.deleteCategory(selectedCategory.id);
      setCategories(prev => prev.filter(cat => cat.id !== selectedCategory.id));
      addToast('Categoria excluída com sucesso!', 'success');
      setShowDeleteModal(false);
      setSelectedCategory(null);
    } catch (error) {
      addToast('Erro ao excluir categoria. Tente novamente.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateSuccess = () => {
    loadCategories();
  };

  const handleEditSuccess = () => {
    loadCategories();
  };

  const handleViewDocuments = (category: Category) => {
    setSelectedCategoryForDocuments(category);
    setShowDocumentList(true);
  };

  const handleBackFromDocuments = () => {
    setShowDocumentList(false);
    setSelectedCategoryForDocuments(null);
  };

  const handleNodeSelect = (node: DocumentNode) => {
    // Implementar navegação para o documento selecionado
    addToast('Funcionalidade de navegação será implementada', 'info');
  };

  const getTypeTranslation = (type: string): string => {
    switch (type) {
      case 'document':
        return 'Documentos';
      case 'knowledge':
        return 'Conhecimento';
      default:
        return type;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Se estiver mostrando a listagem de documentos
  if (showDocumentList && selectedCategoryForDocuments) {
    return (
      <DocumentListByFilter
        filterType="category"
        filterId={selectedCategoryForDocuments.id}
        filterName={selectedCategoryForDocuments.name}
        onBack={() => setShowDocumentList(false)}
        onNodeSelect={handleNodeSelect}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categorias de Documentos</h1>
          <p className="text-gray-600">Gerencie as categorias para organizar seus documentos</p>
        </div>
        <button
          onClick={handleCreateCategory}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nova Categoria
        </button>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <div className="relative">
          <input
            type="text"
            placeholder="Buscar categorias..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCategories.map((category) => (
          <div
            key={category.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg mr-3"
                  style={{ backgroundColor: '#3B82F6' }}
                >
                  {category.icon || '📄'}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                  <p className="text-sm text-gray-600">{category.description || 'Sem descrição'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleEditCategory(category)}
                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Editar categoria"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteCategory(category)}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Excluir categoria"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Documentos:</span>
                <span className="font-medium text-gray-900">{category.documentCount}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Tipo:</span>
                <span className="font-medium text-gray-900">{getTypeTranslation(category.type)}</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => handleViewDocuments(category)}
                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
                title="Ver documentos desta categoria"
              >
                <EyeIcon className="h-4 w-4 mr-1" />
                Ver documentos
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredCategories.length === 0 && (
        <div className="text-center py-12">
          <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma categoria encontrada</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Tente ajustar os termos de busca.' : 'Comece criando sua primeira categoria.'}
          </p>
        </div>
      )}

      {/* Modals */}
      <CreateCategoryModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      <EditCategoryModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleEditSuccess}
        category={selectedCategory}
      />

      <DeleteCategoryModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedCategory(null);
        }}
        onConfirm={handleConfirmDelete}
        category={selectedCategory}
        isLoading={isDeleting}
      />
    </div>
  );
}; 